import { Badge, Box, Button, Flex, HStack, Text } from '@elkaso-app/web-design';
import { useAdaptPriceImportRunApi, useGetPriceImportRowsApi, useGetPriceImportRunApi } from 'apis/price-imports';
import { BaseTableContainer } from 'components/base-table';
import { usePageParams } from 'hooks/use-page-params';
import { ApprovalsPanel } from 'pages/price-imports/components/approvals-panel';
import { CommitPanel } from 'pages/price-imports/components/commit-panel';
import { MappingEditor } from 'pages/price-imports/components/mapping-editor';
import { MappingGuidance } from 'pages/price-imports/components/mapping-guidance';
import { RowReviewPanel } from 'pages/price-imports/components/row-review-panel';
import { defaultStepFor, isRunReadOnly, runStatusPresentation, RunStep } from 'pages/price-imports/utils/run-status';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

const STEPS: { key: RunStep; label: string }[] = [
  { key: 'mapping', label: 'Mapping' },
  { key: 'rows', label: 'Rows' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'commit', label: 'Commit' },
];

/**
 * One durable import run, its body switched by a `?step=` URL param.
 *
 * URL-param body switching rather than component state, matching the Prices
 * screen's `view` param: it buys back/forward, a deep link to the row someone
 * is stuck on, and survival across a refresh. The run is durable server-side,
 * so the screen has to be re-enterable too.
 *
 * Tabs mechanically, wizard semantically: steps are always clickable backwards,
 * because fixing the mapping after seeing the rows is a real workflow.
 */
export const PriceImportRunScreen = () => {
  const dispatch = useAppDispatch();
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const runId = Number(runIdParam);

  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const { data, isLoading } = useGetPriceImportRunApi(runId);
  const { mutateAsync: adaptRun, isLoading: isAdapting } = useAdaptPriceImportRunApi();

  const run = data?.data;
  const { data: rowsData, isLoading: isLoadingRows } = useGetPriceImportRowsApi(runId);
  const rows = rowsData?.data ?? [];

  // Returning to a run lands where the work is, not always at step one.
  const step = (params.step as RunStep) || (run ? defaultStepFor(run.status) : 'rows');

  useEffect(() => {
    dispatch(changeTitle('Price Import'));
  }, []);

  if (isLoading || !run) return <BaseTableContainer isLoading />;

  const presentation = runStatusPresentation(run.status);
  const readOnly = isRunReadOnly(run.status);

  return (
    <BaseTableContainer isLoading={false}>
      <Box p='16px'>
        <Flex justify='space-between' align='flex-start' mb='12px' gap='16px'>
          <Box>
            <HStack spacing='8px' mb='4px'>
              <Link to='/app/price-imports/list'>
                <Text fontSize='xs' color='red.500'>
                  ← All imports
                </Text>
              </Link>
            </HStack>
            <HStack spacing='8px'>
              <Text fontSize='lg' fontWeight='semibold'>
                {run.originalFileName}
              </Text>
              <Badge colorScheme={presentation.colorScheme} variant='subtle'>
                {presentation.label}
              </Badge>
            </HStack>
            <Text fontSize='xs' color='gray.500'>
              Supplier #{run.supplierId} · {run.totalRows} rows · {run.matchedRows} matched · {run.reviewRows} to review
              {run.proposalRows > 0 ? ` · ${run.proposalRows} new` : ''}
            </Text>
          </Box>

          {run.status === 'adapt_failed' && (
            <Button size='sm' colorScheme='red' isLoading={isAdapting} onClick={() => adaptRun({ runId })}>
              Try reading again
            </Button>
          )}
        </Flex>

        {run.lastError && (
          <Box bg='orange.50' border='1px solid' borderColor='orange.200' borderRadius='8px' p='10px' mb='12px'>
            <Text fontSize='sm' color='orange.800'>
              {run.lastError}
            </Text>
          </Box>
        )}

        <HStack spacing='4px' mb='16px'>
          {STEPS.map((entry) => (
            <Button
              key={entry.key}
              size='sm'
              variant={step === entry.key ? 'solid' : 'ghost'}
              colorScheme={step === entry.key ? 'red' : 'gray'}
              onClick={() => setPageParams({ ...getPageParams(), step: entry.key })}>
              {entry.label}
            </Button>
          ))}
        </HStack>

        {step === 'rows' && <RowReviewPanel runId={runId} rows={rows} isLoading={isLoadingRows} readOnly={readOnly} />}

        {step === 'mapping' && (
          <>
            <MappingGuidance run={run} />
            <MappingEditor run={run} readOnly={readOnly} />
          </>
        )}

        {step === 'approvals' && (
          <ApprovalsPanel
            runId={runId}
            rows={rows}
            readOnly={readOnly}
            // Jumping straight to the rows behind a proposal: the operator
            // asked "which lines is this for", so answer it in the grid.
            onReviewRows={() => setPageParams({ ...getPageParams(), step: 'rows', bucket: 'decide', rowId: undefined })}
          />
        )}

        {step === 'commit' && <CommitPanel run={run} />}
      </Box>
    </BaseTableContainer>
  );
};
